import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";

// ─── IMAGES ──────────────────────────────────────────────────────────────────
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1800&q=80",
  team: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1800&q=80",
  office:
    "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1800&q=80",
  awards:
    "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1800&q=80",
  history:
    "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1800&q=80",
  mission:
    "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1800&q=80",
  values:
    "https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1800&q=80",
  cta: "https://images.unsplash.com/photo-1600210492493-0946911123ea?w=1800&q=80",
};

// ─── EMAILJS CONFIGURATION ──────────────────────────────────────────────────
const EMAILJS_CONFIG = {
  SERVICE_ID: "service_somu38y",
  TEMPLATE_ID: "template_jvsn8tq",
  PUBLIC_KEY: "nbgiKhgvRrvIMJUfQ",
};

// ─── SVG ICONS ────────────────────────────────────────────────────────────────
const IconPin = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IconArrow = ({ dir = "right", size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: dir === "left" ? "rotate(180deg)" : "none",
      flexShrink: 0,
    }}
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const IconPhone = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.53 2 2 0 0 1 3.56 1.35h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z" />
  </svg>
);

const IconWhatsapp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.115 1.528 5.845L.057 23.926l6.234-1.635A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.885 0-3.64-.52-5.146-1.422l-.369-.217-3.813 1 1.018-3.716-.24-.38A9.96 9.96 0 0 1 2 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z" />
  </svg>
);

const IconClose = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// ─── ANIMATED SECTION COMPONENT ─────────────────────────────────────────────
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay * 1000);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "50px" },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={sectionRef}
      className={`animated-section ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
};

// ─── DELAYED ANIMATED SECTION ──────────────────────────────────────────────
const DelayedSection = ({ children, delay = 0, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Add a fixed delay after the section comes into view
          setTimeout(() => {
            setIsVisible(true);
          }, delay * 1000);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "50px" },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={sectionRef}
      className={`delayed-section ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function About() {
  const PHONE_NUMBER = "+918584826222";
  const PHONE_DISPLAY = "+91 85848 26222";

  const [contactStep, setContactStep] = useState(1);
  const [contactInterest, setContactInterest] = useState("");
  const [contactBeds, setContactBeds] = useState("");
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
  }, []);

  // ─── CONTACT FORM FUNCTIONS ──────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName?.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName?.trim())
      newErrors.lastName = "Last name is required";
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (
      !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\D/g, ""))
    ) {
      newErrors.phone = "Please enter a valid phone number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitContact = async () => {
    if (!validateForm()) {
      setToast("❌ Please fill in all required fields");
      setTimeout(() => setToast(""), 2800);
      return;
    }

    setIsSubmitting(true);

    try {
      const templateParams = {
        interest: contactInterest || "General Inquiry",
        from_name: `${formData.firstName} ${formData.lastName}`.trim(),
        from_email: formData.email.trim(),
        from_phone: formData.phone.trim(),
        bedrooms: contactBeds || "Not specified",
        message: formData.message?.trim() || "No additional message provided",
        date: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY,
      );

      console.log("EmailJS success:", response);
      setSubmitted(true);
      setToast("✅ Thank you! Your request has been submitted successfully.");

      setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
        });
        setErrors({});
        setContactStep(1);
        setContactInterest("");
        setContactBeds("");
        setContactModalOpen(false);
        setSubmitted(false);
        setTimeout(() => setToast(""), 2000);
      }, 1500);
    } catch (error) {
      console.error("EmailJS error:", error);
      setIsSubmitting(false);

      const useMailto = window.confirm(
        "❌ Email service is temporarily unavailable.\n\n" +
          "Would you like to send your inquiry directly via your email app instead?\n\n" +
          "✓ Click OK to open your email app with all details pre-filled\n" +
          "✓ Click Cancel to close and try again later",
      );

      if (useMailto) {
        const subject = `Property Inquiry: ${contactInterest || "New Inquiry"} - ${formData.firstName} ${formData.lastName}`;
        const body = `
NEXT.FRONT PROPERTIES - NEW INQUIRY
====================================
Interest: ${contactInterest || "General Inquiry"}
Bedrooms: ${contactBeds || "Not specified"}

CONTACT DETAILS
------------------------------------
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone}

MESSAGE
------------------------------------
${formData.message || "No message provided"}

Submitted: ${new Date().toLocaleString()}
        `.trim();
        window.location.href = `mailto:info@nextfrontproperties.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setToast("📧 Opening your email app...");
        setTimeout(() => setToast(""), 2800);
        setContactModalOpen(false);
      } else {
        setToast("❌ Submission cancelled. Please try again later.");
        setTimeout(() => setToast(""), 2800);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openContactModal = (interest, propertyName = "") => {
    setContactInterest(
      propertyName ? `${interest} - ${propertyName}` : interest,
    );
    setContactStep(3);
    setContactModalOpen(true);
    document.body.style.overflow = "hidden";
    setSubmitted(false);
    setErrors({});
  };

  const closeContactModal = () => {
    setContactModalOpen(false);
    document.body.style.overflow = "unset";
    setTimeout(() => {
      if (!submitted) {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
        });
        setErrors({});
        setContactStep(1);
        setContactInterest("");
        setContactBeds("");
      }
    }, 400);
  };

  const handleInterestSelect = (value) => {
    setContactInterest(value);
    setContactStep(2);
  };

  const handleBedSelect = (value) => {
    setContactBeds(value);
    setContactStep(3);
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${PHONE_NUMBER}`, "_blank");
  };

  return (
    <div
      style={{
        fontFamily: "'DM Sans', sans-serif",
        background: "#07101e",
        color: "#e8e0d0",
        minHeight: "100vh",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --gold: #C9A84C;
          --gold-light: #E8C96A;
          --gold-dim: rgba(201,168,76,0.14);
          --dark: #07101e;
          --dark2: #0d1a2d;
          --dark3: #111f33;
          --text: #e8e0d0;
          --text2: rgba(232,224,208,0.55);
          --text3: rgba(232,224,208,0.3);
          --border: rgba(201,168,76,0.14);
          --card: rgba(255,255,255,0.04);
          --r: 6px;
          --rl: 10px;
        }

        html { scroll-behavior: smooth; }

        /* ── ANIMATIONS ── */
        .animated-section {
          opacity: 0;
          transform: translateY(50px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .animated-section.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .delayed-section {
          opacity: 0;
          transform: translateY(60px);
          transition: all 0.9s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .delayed-section.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── HERO ── */
        .ab-hero {
          min-height: 70vh;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          overflow: hidden;
        }
        .ab-hero-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          opacity: 0.60;
          z-index: 0;
        }
        .ab-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(7,16,30,0.2) 0%, rgba(13,26,45,0.1) 50%, rgba(7,16,30,0.02) 100%);
          z-index: 1;
        }
        .ab-hero-content {
          position: relative;
          z-index: 2;
          max-width: 900px;
          padding: 140px 48px 80px;
          width: 100%;
        }
        .ab-hero-eyebrow {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 4px;
          color: var(--gold);
          text-transform: uppercase;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
        }
        .ab-hero-eyebrow::before,
        .ab-hero-eyebrow::after {
          content: '';
          width: 40px;
          height: 1px;
          background: var(--gold);
        }
        .ab-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(48px, 7vw, 80px);
          font-weight: 700;
          color: #f0e8d8;
          line-height: 1.05;
          margin-bottom: 24px;
        }
        .ab-hero-title em {
          color: var(--gold);
          font-style: italic;
        }
        .ab-hero-desc {
          font-size: 18px;
          line-height: 1.9;
          color: var(--text2);
          max-width: 700px;
          margin: 0 auto 40px;
        }

        /* ── STATS ── */
        .ab-stats {
          background: var(--dark2);
          border-top: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
          padding: 0 48px;
        }
        .ab-stats-inner {
          max-width: 1240px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4,1fr);
        }
        .ab-stat-item {
          text-align: center;
          padding: 52px 16px;
          border-right: 1px solid var(--border);
        }
        .ab-stat-item:last-child { border-right: none; }
        .ab-stat-val {
          font-family: 'Playfair Display', serif;
          font-size: clamp(42px, 5vw, 60px);
          font-weight: 700;
          color: var(--gold);
          line-height: 1;
          margin-bottom: 8px;
        }
        .ab-stat-lbl {
          font-size: 12px;
          color: var(--text3);
          letter-spacing: 2px;
          text-transform: uppercase;
          font-weight: 600;
        }

        /* ── SECTION COMMON ── */
        .ab-section {
          max-width: 1240px;
          margin: 0 auto;
          padding: 80px 48px;
          position: relative;
          z-index: 2;
        }
        .ab-eyebrow {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 3.5px;
          color: var(--gold);
          text-transform: uppercase;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ab-eyebrow::before {
          content: '';
          width: 28px;
          height: 1px;
          background: var(--gold);
        }
        .ab-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(32px, 4vw, 46px);
          font-weight: 700;
          color: #f0e8d8;
          margin-bottom: 16px;
          line-height: 1.15;
        }
        .ab-title strong {
          color: var(--gold);
        }
        .ab-sub {
          font-size: 15px;
          line-height: 1.85;
          color: var(--text2);
          max-width: 620px;
        }
        .ab-header { margin-bottom: 48px; }

        /* ── ABOUT GRID ── */
        .ab-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: center;
        }
        .ab-image-wrap {
          position: relative;
          border-radius: var(--rl);
          overflow: hidden;
        }
        .ab-image-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .ab-image-badge {
          position: absolute;
          bottom: 24px;
          left: 24px;
          background: var(--gold);
          color: var(--dark);
          padding: 16px 24px;
          border-radius: var(--r);
          text-align: center;
        }
        .ab-image-badge-num {
          font-family: 'Playfair Display', serif;
          font-size: 32px;
          font-weight: 700;
          line-height: 1;
        }
        .ab-image-badge-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        /* ── MISSION & VISION ── */
        .ab-mv-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          margin-top: 32px;
        }
        .ab-mv-card {
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          padding: 36px 32px;
          transition: all 0.3s ease;
        }
        .ab-mv-card:hover {
          border-color: var(--gold);
          transform: translateY(-4px);
          background: rgba(201,168,76,0.05);
        }
        .ab-mv-icon {
          font-size: 40px;
          margin-bottom: 16px;
          display: block;
        }
        .ab-mv-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 700;
          color: #f0e8d8;
          margin-bottom: 10px;
        }
        .ab-mv-desc {
          font-size: 14px;
          line-height: 1.8;
          color: var(--text2);
        }

        /* ── TEAM ── */
        .ab-team-grid {
          display: grid;
          grid-template-columns: repeat(3,1fr);
          gap: 32px;
          margin-top: 32px;
        }
        .ab-team-card {
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          overflow: hidden;
          transition: all 0.3s ease;
          text-align: center;
        }
        .ab-team-card:hover {
          border-color: var(--gold);
          transform: translateY(-4px);
        }
        .ab-team-img {
          width: 100%;
          height: 280px;
          object-fit: cover;
        }
        .ab-team-info {
          padding: 24px 20px;
        }
        .ab-team-name {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 700;
          color: #f0e8d8;
          margin-bottom: 4px;
        }
        .ab-team-role {
          font-size: 13px;
          color: var(--gold);
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .ab-team-desc {
          font-size: 13px;
          color: var(--text3);
          margin-top: 10px;
          line-height: 1.7;
        }

        /* ── VALUES ── */
        .ab-values-grid {
          display: grid;
          grid-template-columns: repeat(3,1fr);
          gap: 24px;
          margin-top: 32px;
        }
        .ab-value-card {
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          padding: 32px 24px;
          text-align: center;
          transition: all 0.3s ease;
        }
        .ab-value-card:hover {
          border-color: var(--gold);
          transform: translateY(-4px);
        }
        .ab-value-icon {
          font-size: 42px;
          margin-bottom: 16px;
          display: block;
        }
        .ab-value-title {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 700;
          color: #f0e8d8;
          margin-bottom: 8px;
        }
        .ab-value-desc {
          font-size: 14px;
          color: var(--text2);
          line-height: 1.7;
        }

        /* ── TIMELINE ── */
        .ab-timeline {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin-top: 40px;
          position: relative;
          padding-left: 40px;
        }
        .ab-timeline::before {
          content: '';
          position: absolute;
          left: 10px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: var(--border);
        }
        .ab-timeline-item {
          position: relative;
          padding: 0 0 40px 40px;
        }
        .ab-timeline-item::before {
          content: '';
          position: absolute;
          left: -34px;
          top: 8px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--gold);
          border: 3px solid var(--dark);
        }
        .ab-timeline-year {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 700;
          color: var(--gold);
        }
        .ab-timeline-title {
          font-size: 18px;
          font-weight: 600;
          color: #f0e8d8;
          margin: 4px 0;
        }
        .ab-timeline-desc {
          font-size: 14px;
          color: var(--text2);
          line-height: 1.7;
        }

        /* ── CTA ── */
        .ab-cta {
          background: var(--dark2);
          border-top: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
          padding: 80px 48px;
          text-align: center;
        }
        .ab-cta-inner {
          max-width: 700px;
          margin: 0 auto;
        }
        .ab-cta-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(32px, 4vw, 44px);
          font-weight: 700;
          color: #f0e8d8;
          margin-bottom: 16px;
        }
        .ab-cta-title strong {
          color: var(--gold);
        }
        .ab-cta-desc {
          font-size: 16px;
          color: var(--text2);
          line-height: 1.8;
          margin-bottom: 32px;
        }
        .ab-cta-btns {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* ── BUTTONS ── */
        .btn-gold {
          background: var(--gold);
          color: var(--dark);
          border: none;
          padding: 14px 32px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: var(--r);
          transition: all 0.22s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .btn-gold:hover {
          background: var(--gold-light);
          gap: 12px;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(201,168,76,0.3);
        }
        .btn-ghost {
          background: transparent;
          color: var(--gold);
          border: 1px solid rgba(201,168,76,0.3);
          padding: 14px 32px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: var(--r);
          transition: all 0.22s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .btn-ghost:hover {
          border-color: var(--gold);
          background: var(--gold-dim);
          transform: translateY(-2px);
        }

        /* ── TOAST ── */
        .ab-toast {
          position: fixed;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--dark2);
          border: 1px solid var(--gold);
          color: var(--text);
          padding: 14px 28px;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.5px;
          z-index: 99999;
          display: flex;
          align-items: center;
          gap: 10px;
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
          animation: toastIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          backdrop-filter: blur(10px);
        }
        @keyframes toastIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0) scale(1);
          }
        }
        @media (max-width: 480px) {
          .ab-toast {
            font-size: 12px;
            padding: 12px 20px;
            bottom: 24px;
            white-space: normal;
            max-width: 90%;
            text-align: center;
          }
        }

        /* ── CONTACT MODAL ── */
        .ab-contact-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.92);
          backdrop-filter: blur(12px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .ab-contact-modal-content {
          background: linear-gradient(145deg, #0d1a2d 0%, #07101e 100%);
          border: 1px solid rgba(201,168,76,0.2);
          border-radius: 16px;
          max-width: 560px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
          padding: 40px 36px;
        }
        .ab-contact-modal-content::-webkit-scrollbar {
          width: 4px;
        }
        .ab-contact-modal-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .ab-contact-modal-content::-webkit-scrollbar-thumb {
          background: var(--gold);
          border-radius: 2px;
        }
        .ab-contact-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
        }
        .ab-contact-modal-close:hover {
          background: rgba(201,168,76,0.2);
          border-color: var(--gold);
          transform: rotate(90deg);
        }
        .ab-contact-modal-close svg {
          width: 20px;
          height: 20px;
        }
        .ab-contact-steps {
          display: flex;
          gap: 8px;
          margin-bottom: 28px;
          justify-content: center;
        }
        .ab-contact-step-dot {
          width: 30px;
          height: 3px;
          border-radius: 2px;
          background: var(--border);
          transition: background 0.3s;
        }
        .ab-contact-step-dot.active {
          background: var(--gold);
        }
        .ab-form-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: var(--r);
          padding: 11px 14px;
          font-size: 13px;
          color: var(--text);
          font-family: 'DM Sans', sans-serif;
          outline: none;
          transition: border-color 0.2s;
          width: 100%;
        }
        .ab-form-input:focus {
          border-color: rgba(201,168,76,0.4);
        }
        .ab-form-input::placeholder {
          color: var(--text3);
        }
        .ab-form-input.error {
          border-color: #ff6b6b !important;
        }
        .ab-form-input.error:focus {
          border-color: #ff6b6b !important;
          box-shadow: 0 0 0 1px #ff6b6b;
        }
        .ab-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        @media (max-width: 480px) {
          .ab-form-row { grid-template-columns: 1fr; }
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) {
          .ab-hero-content { padding: 120px 32px 60px; }
          .ab-section { padding: 60px 32px; }
          .ab-grid { grid-template-columns: 1fr; gap: 40px; }
          .ab-team-grid { grid-template-columns: repeat(2,1fr); }
          .ab-values-grid { grid-template-columns: repeat(2,1fr); }
          .ab-mv-grid { grid-template-columns: 1fr; }
          .ab-stats { padding: 0 32px; }
          .ab-cta { padding: 60px 32px; }
        }

        @media (max-width: 768px) {
          .ab-hero-content { padding: 100px 24px 48px; }
          .ab-section { padding: 48px 24px; }
          .ab-stats-inner { grid-template-columns: repeat(2,1fr); }
          .ab-stat-item { padding: 32px 16px; border-right: none; border-bottom: 1px solid var(--border); }
          .ab-stat-item:nth-child(odd) { border-right: 1px solid var(--border); }
          .ab-stat-item:last-child { border-bottom: none; }
          .ab-team-grid { grid-template-columns: 1fr; max-width: 400px; margin: 0 auto; }
          .ab-values-grid { grid-template-columns: 1fr; max-width: 400px; margin: 0 auto; }
          .ab-timeline { padding-left: 28px; }
          .ab-timeline-item { padding: 0 0 30px 28px; }
          .ab-timeline-item::before { left: -24px; width: 12px; height: 12px; }
          .ab-cta { padding: 48px 24px; }
          .ab-cta-btns { flex-direction: column; align-items: center; }
          .ab-cta-btns button { width: 100%; justify-content: center; }
          .ab-hero-eyebrow::before,
          .ab-hero-eyebrow::after { width: 24px; }
          .ab-image-badge { padding: 12px 18px; }
          .ab-image-badge-num { font-size: 24px; }
        }

        @media (max-width: 480px) {
          .ab-hero-content { padding: 80px 16px 32px; }
          .ab-hero-title { font-size: clamp(32px, 8vw, 40px); }
          .ab-hero-desc { font-size: 15px; }
          .ab-section { padding: 32px 16px; }
          .ab-stats { padding: 0 16px; }
          .ab-stat-item { padding: 24px 12px; }
          .ab-stat-val { font-size: 28px; }
          .ab-stat-lbl { font-size: 10px; }
          .ab-contact-modal-content { padding: 28px 20px; margin: 10px; }
          .ab-timeline { padding-left: 20px; }
          .ab-timeline-item { padding: 0 0 24px 20px; }
          .ab-timeline-item::before { left: -18px; width: 10px; height: 10px; }
        }

        /* Touch Device Optimizations */
        @media (hover: none) {
          .ab-mv-card:hover,
          .ab-team-card:hover,
          .ab-value-card:hover {
            transform: none;
          }
          .ab-mv-card:active,
          .ab-team-card:active,
          .ab-value-card:active {
            transform: scale(0.98);
          }
        }

        /* Print Styles */
        @media print {
          .ab-hero-bg { opacity: 0.50; }
          .ab-hero-overlay { background: rgba(0,0,0,0.1); }
          .btn-gold, .btn-ghost { background: #333 !important; color: #fff !important; border: 1px solid #666 !important; }
          .ab-cta { border: 1px solid #ccc; }
          .ab-contact-modal-overlay { display: none; }
          .animated-section { opacity: 1 !important; transform: none !important; }
          .delayed-section { opacity: 1 !important; transform: none !important; }
        }
      `}</style>

      {/* ─── HERO SECTION ── */}
      <section className="ab-hero">
        <div
          className="ab-hero-bg"
          style={{
            backgroundImage: `url(${IMAGES.hero})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div className="ab-hero-overlay" />
        <div className="ab-hero-content">
          <div className="ab-hero-eyebrow">About NextFront</div>
          <h1 className="ab-hero-title">
            Building Dreams,
            <br />
            <em>One Home at a Time</em>
          </h1>
          <p className="ab-hero-desc">
            With over 35 years of excellence in real estate, NextFront
            Properties has been transforming the landscape of Kolkata and Dubai.
            We combine deep market knowledge with unwavering integrity to help
            you find your perfect home.
          </p>
        </div>
      </section>

      <div className="ab-section">
        <DelayedSection delay={1.0}>
          <div className="ab-grid">
            <div className="ab-image-wrap">
              <img
                src={IMAGES.history}
                alt="Our Story"
                style={{ height: "480px", width: "100%", objectFit: "cover" }}
              />
              <div className="ab-image-badge">
                <div className="ab-image-badge-num">35+</div>
                <div className="ab-image-badge-label">Years of Trust</div>
              </div>
            </div>
            <div>
              <div className="ab-eyebrow">Our Story</div>
              <h2 className="ab-title">
                A Legacy of <strong>Trust & Excellence</strong>
              </h2>
              <p className="ab-sub" style={{ marginBottom: 24 }}>
                Founded in 1988, NextFront Properties began with a simple
                mission: to make the dream of homeownership accessible to every
                family. Over the decades, we've grown into one of Kolkata's most
                trusted real estate brands, expanding our reach to Dubai's
                luxury market.
              </p>
              <p className="ab-sub" style={{ marginBottom: 32 }}>
                Our journey is built on relationships — with our clients,
                partners, and the communities we serve. We believe that a home
                is more than just bricks and mortar; it's where memories are
                made and futures are built.
              </p>
              <Link to="/">
                <button className="btn-gold">
                  Explore Our Properties <IconArrow />
                </button>
              </Link>
            </div>
          </div>
        </DelayedSection>
      </div>

      {/* ─── MISSION & VISION ── */}
      <div
        className="ab-section"
        style={{
          background: "var(--dark2)",
          borderRadius: "12px",
          marginTop: 0,
        }}
      >
        <DelayedSection delay={1.0}>
          <div className="ab-header" style={{ textAlign: "center" }}>
            <div className="ab-eyebrow" style={{ justifyContent: "center" }}>
              Our Purpose
            </div>
            <h2 className="ab-title" style={{ textAlign: "center" }}>
              Mission, Vision &amp; <strong>Values</strong>
            </h2>
          </div>
          <div className="ab-mv-grid">
            <div className="ab-mv-card">
              <span className="ab-mv-icon">🎯</span>
              <h3 className="ab-mv-title">Our Mission</h3>
              <p className="ab-mv-desc">
                To provide exceptional real estate services with integrity,
                transparency, and a deep commitment to our clients' needs. We
                strive to make property transactions seamless, stress-free, and
                rewarding.
              </p>
            </div>
            <div className="ab-mv-card">
              <span className="ab-mv-icon">👁️</span>
              <h3 className="ab-mv-title">Our Vision</h3>
              <p className="ab-mv-desc">
                To become the most trusted real estate partner in India and
                Dubai, known for unparalleled expertise, innovative solutions,
                and a unwavering commitment to client satisfaction.
              </p>
            </div>
          </div>
        </DelayedSection>
      </div>

      {/* ─── VALUES ── */}
      <div className="ab-section">
        <DelayedSection delay={1.0}>
          <div className="ab-header" style={{ textAlign: "center" }}>
            <div className="ab-eyebrow" style={{ justifyContent: "center" }}>
              What Drives Us
            </div>
            <h2 className="ab-title" style={{ textAlign: "center" }}>
              Our Core <strong>Values</strong>
            </h2>
            <p
              className="ab-sub"
              style={{ margin: "0 auto", textAlign: "center" }}
            >
              These principles guide every decision we make and every
              interaction we have with our clients.
            </p>
          </div>
          <div className="ab-values-grid">
            <div className="ab-value-card">
              <span className="ab-value-icon">🤝</span>
              <h3 className="ab-value-title">Integrity</h3>
              <p className="ab-value-desc">
                We believe in honest, transparent dealings. Our reputation is
                built on trust, and we protect it with every interaction.
              </p>
            </div>
            <div className="ab-value-card">
              <span className="ab-value-icon">⭐</span>
              <h3 className="ab-value-title">Excellence</h3>
              <p className="ab-value-desc">
                From market analysis to property management, we pursue
                excellence in every aspect of our work.
              </p>
            </div>
            <div className="ab-value-card">
              <span className="ab-value-icon">🤗</span>
              <h3 className="ab-value-title">Client First</h3>
              <p className="ab-value-desc">
                Our clients are at the heart of everything we do. Their success
                and satisfaction are our ultimate goals.
              </p>
            </div>
          </div>
        </DelayedSection>
      </div>

      {/* ─── TEAM ── */}
      {/* <div className="ab-section" style={{ background: "var(--dark2)", borderRadius: "12px" }}>
        <DelayedSection delay={1.0}>
          <div className="ab-header" style={{ textAlign: "center" }}>
            <div className="ab-eyebrow" style={{ justifyContent: "center" }}>Our Team</div>
            <h2 className="ab-title" style={{ textAlign: "center" }}>Meet Our <strong>Experts</strong></h2>
            <p className="ab-sub" style={{ margin: "0 auto", textAlign: "center" }}>
              A dedicated team of professionals committed to helping you find your dream property.
            </p>
          </div>
          <div className="ab-team-grid">
            <div className="ab-team-card">
              <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80" alt="Team Member" className="ab-team-img" />
              <div className="ab-team-info">
                <h3 className="ab-team-name">Rajiv Kumar</h3>
                <div className="ab-team-role">Founder &amp; CEO</div>
                <p className="ab-team-desc">35+ years of experience in real estate, visionary leader behind NextFront Properties.</p>
              </div>
            </div>
            <div className="ab-team-card">
              <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&q=80" alt="Team Member" className="ab-team-img" />
              <div className="ab-team-info">
                <h3 className="ab-team-name">Priya Sharma</h3>
                <div className="ab-team-role">Head of Sales</div>
                <p className="ab-team-desc">15+ years in luxury real estate, expert in client relations and property valuation.</p>
              </div>
            </div>
            <div className="ab-team-card">
              <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&q=80" alt="Team Member" className="ab-team-img" />
              <div className="ab-team-info">
                <h3 className="ab-team-name">Amit Das</h3>
                <div className="ab-team-role">Senior Advisor</div>
                <p className="ab-team-desc">Specialist in Dubai luxury properties with deep knowledge of global real estate trends.</p>
              </div>
            </div>
          </div>
        </DelayedSection>
      </div> */}

      {/* ─── TIMELINE ── */}
      <div className="ab-section">
        <DelayedSection delay={1.0}>
          <div className="ab-header">
            <div className="ab-eyebrow">Our Journey</div>
            <h2 className="ab-title">
              A Legacy of <strong>Milestones</strong>
            </h2>
          </div>
          <div className="ab-timeline">
            <div className="ab-timeline-item">
              <div className="ab-timeline-year">2020</div>
              <div className="ab-timeline-title">Pandemic Resilience</div>
              <div className="ab-timeline-desc">
                Adapted to challenges with virtual property tours and
                digital-first client engagement.
              </div>
            </div>
            <div className="ab-timeline-item">
              <div className="ab-timeline-year">2021</div>
              <div className="ab-timeline-title">Record Growth</div>
              <div className="ab-timeline-desc">
                Achieved remarkable growth despite market uncertainties.
              </div>
            </div>
            <div className="ab-timeline-item">
              <div className="ab-timeline-year">2022</div>
              <div className="ab-timeline-title">Smart Living Initiatives</div>
              <div className="ab-timeline-desc">
                Launched tech-enabled smart home solutions for modern buyers.
              </div>
            </div>
            <div className="ab-timeline-item">
              <div className="ab-timeline-year">2023</div>
              <div className="ab-timeline-title">Dubai Expansion</div>
              <div className="ab-timeline-desc">
                Launched Dubai operations, bringing luxury properties to our
                discerning clients.
              </div>
            </div>
            <div className="ab-timeline-item">
              <div className="ab-timeline-year">2024</div>
              <div className="ab-timeline-title">Sustainability Focus</div>
              <div className="ab-timeline-desc">
                Pioneered eco-friendly and sustainable real estate projects.
              </div>
            </div>
            <div className="ab-timeline-item">
              <div className="ab-timeline-year">2025</div>
              <div className="ab-timeline-title">Industry Leadership</div>
              <div className="ab-timeline-desc">
                Recognized as a trusted name in real estate across Kolkata and
                Dubai.
              </div>
            </div>
          </div>
        </DelayedSection>
      </div>

      {/* ─── CTA SECTION ── */}
      <div className="ab-cta">
        <div className="ab-cta-inner">
          <DelayedSection delay={1.0}>
            <h2 className="ab-cta-title">
              Ready to Find Your <strong>Dream Home</strong>?
            </h2>
            <p className="ab-cta-desc">
              Whether you're looking for your first home, an investment
              property, or a luxury villa in Dubai, our team is here to guide
              you every step of the way.
            </p>
            <div className="ab-cta-btns">
              <button
                className="btn-gold"
                onClick={() => openContactModal("Property Inquiry")}
              >
                Start Your Journey <IconArrow />
              </button>
              <a
                href={`tel:${PHONE_NUMBER}`}
                className="btn-ghost"
                style={{ textDecoration: "none" }}
              >
                <IconPhone /> Call Us Now
              </a>
            </div>
          </DelayedSection>
        </div>
      </div>

      {contactModalOpen && (
        <div className="ab-contact-modal-overlay" onClick={closeContactModal}>
          <div
            className="ab-contact-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="ab-contact-modal-close"
              onClick={closeContactModal}
            >
              <IconClose />
            </button>

            {!submitted && (
              <>
                <div className="ab-contact-steps">
                  <div
                    className={`ab-contact-step-dot ${contactStep >= 1 ? "active" : ""}`}
                  />
                  <div
                    className={`ab-contact-step-dot ${contactStep >= 2 ? "active" : ""}`}
                  />
                  <div
                    className={`ab-contact-step-dot ${contactStep >= 3 ? "active" : ""}`}
                  />
                </div>

                {contactStep === 1 && (
                  <div>
                    <h3
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: 22,
                        color: "#f0e8d8",
                        marginBottom: 6,
                        textAlign: "center",
                      }}
                    >
                      Express Your Interest
                    </h3>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--text3)",
                        textAlign: "center",
                        marginBottom: 24,
                      }}
                    >
                      Select what you're looking for
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      {[
                        { icon: "🏠", label: "Buy/Sell" },
                        { icon: "🔑", label: "Rent" },
                        { icon: "📊", label: "Invest" },
                        { icon: "💰", label: "Mortgage" },
                        { icon: "🏗️", label: "Off-Plan" },
                        { icon: "📋", label: "General" },
                      ].map((opt) => (
                        <button
                          key={opt.label}
                          onClick={() => handleInterestSelect(opt.label)}
                          style={{
                            padding: "16px",
                            border: `1px solid ${contactInterest === opt.label ? "var(--gold)" : "var(--border)"}`,
                            borderRadius: "8px",
                            background:
                              contactInterest === opt.label
                                ? "rgba(201,168,76,0.1)"
                                : "transparent",
                            color: "var(--text)",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            textAlign: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "28px",
                              display: "block",
                              marginBottom: "6px",
                            }}
                          >
                            {opt.icon}
                          </span>
                          <span style={{ fontSize: "12px", fontWeight: 600 }}>
                            {opt.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {contactStep === 2 && (
                  <div>
                    <h3
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: 22,
                        color: "#f0e8d8",
                        marginBottom: 6,
                        textAlign: "center",
                      }}
                    >
                      How Many Bedrooms?
                    </h3>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--text3)",
                        textAlign: "center",
                        marginBottom: 24,
                      }}
                    >
                      Select your preference for {contactInterest}
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5,1fr)",
                        gap: "8px",
                      }}
                    >
                      {["1", "2", "3", "4", "5+"].map((b) => (
                        <button
                          key={b}
                          onClick={() => handleBedSelect(b)}
                          style={{
                            padding: "14px",
                            border: `1px solid ${contactBeds === b ? "var(--gold)" : "var(--border)"}`,
                            borderRadius: "8px",
                            background:
                              contactBeds === b
                                ? "rgba(201,168,76,0.1)"
                                : "transparent",
                            color: "var(--text)",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            textAlign: "center",
                            fontSize: "18px",
                            fontWeight: 600,
                          }}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                    <button
                      className="btn-ghost"
                      style={{
                        width: "100%",
                        justifyContent: "center",
                        marginTop: "16px",
                      }}
                      onClick={() => setContactStep(1)}
                    >
                      ← Back
                    </button>
                  </div>
                )}

                {contactStep === 3 && (
                  <div>
                    <h3
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: 22,
                        color: "#f0e8d8",
                        marginBottom: 6,
                        textAlign: "center",
                      }}
                    >
                      Your Details
                    </h3>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--text3)",
                        textAlign: "center",
                        marginBottom: 20,
                      }}
                    >
                      We'll reach out within 24 hours
                    </p>
                    <div className="ab-form-row">
                      <input
                        className={`ab-form-input ${errors.firstName ? "error" : ""}`}
                        placeholder="First Name*"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                      />
                      <input
                        className={`ab-form-input ${errors.lastName ? "error" : ""}`}
                        placeholder="Last Name*"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                      />
                    </div>
                    <input
                      className={`ab-form-input ${errors.email ? "error" : ""}`}
                      placeholder="Email Address*"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      style={{ marginBottom: "10px" }}
                    />
                    <input
                      className={`ab-form-input ${errors.phone ? "error" : ""}`}
                      placeholder="Phone Number*"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      style={{ marginBottom: "10px" }}
                    />
                    <textarea
                      className="ab-form-input"
                      placeholder="Your message (optional)..."
                      rows={2}
                      style={{ resize: "vertical", marginBottom: "10px" }}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                    />
                    <div
                      style={{
                        background: "rgba(201,168,76,0.08)",
                        border: "1px solid var(--border)",
                        padding: "10px 14px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        color: "var(--dm)",
                        marginBottom: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <span>Interest:</span>
                        <span style={{ color: "var(--gold)", fontWeight: 600 }}>
                          {contactInterest}
                        </span>
                      </div>
                      {contactBeds && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>Bedrooms:</span>
                          <span
                            style={{ color: "var(--gold)", fontWeight: 600 }}
                          >
                            {contactBeds}
                          </span>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                      }}
                    >
                      <button
                        className="btn-gold"
                        style={{ flex: 1, justifyContent: "center" }}
                        onClick={handleSubmitContact}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "SENDING..." : "SUBMIT"}
                      </button>
                      <button
                        className="btn-ghost"
                        style={{ padding: "12px 20px" }}
                        onClick={() => setContactStep(2)}
                      >
                        ← Back
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {submitted && (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <h3
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 24,
                    color: "#f0e8d8",
                    marginBottom: 8,
                  }}
                >
                  Thank You!
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    color: "var(--text2)",
                    lineHeight: 1.7,
                  }}
                >
                  Our team will contact you within 24 hours to discuss your{" "}
                  {contactInterest} requirements.
                </p>
                <button
                  className="btn-gold"
                  style={{ marginTop: "24px", padding: "12px 28px" }}
                  onClick={() => {
                    setSubmitted(false);
                    setContactStep(1);
                    setContactInterest("");
                    setContactBeds("");
                    setFormData({
                      firstName: "",
                      lastName: "",
                      email: "",
                      phone: "",
                      message: "",
                    });
                    setErrors({});
                    setContactModalOpen(false);
                    document.body.style.overflow = "unset";
                  }}
                >
                  CLOSE
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TOAST ── */}
      {toast && <div className="ab-toast">{toast}</div>}
    </div>
  );
}
